// ============================================================================
// FSA CORPORATE FACTS SERVICE (ADR-0024 §2)
// ============================================================================

import { downloadFile, get, post, put } from '../../client/axiosClient';
import { fsaCorporateFactsEndpoints } from '../../endpoints/fsa/fsaCorporateFacts.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  AgmPackLibrary,
  CorporateFacts,
  CorporateFactsCarryResult,
  MasterDirectorEntry,
  RelatedPartyImportResult,
  SaveCorporateFactsRequest,
  SaveCorporateFactsSectionsRequest,
} from '../../types/fsa/corporateFacts.types';

class FsaCorporateFactsService {
  /** Saved record, or an unsaved empty one (id null, rowVersion 0). */
  async get(engagementId: ID): Promise<ApiResponse<CorporateFacts>> {
    const r = await get<ApiResponse<CorporateFacts>>(fsaCorporateFactsEndpoints.byEngagement(engagementId));
    return r.data;
  }

  /**
   * ADR-0024 A-1 — record answers to Auditor's Report / CARO questions
   * (audit scope). Merged server-side: a value sets, null clears; nothing else
   * in the facts record changes. ADR-0024 N-2 — `scope` "note" stores a note's
   * policy choices (note_answers, carried to next year), "inputs" its figures
   * and text (note_inputs, this year only), "standing" a note's standing
   * inputs (note_standing, carried to next year).
   */
  async setAuditAnswers(
    engagementId: ID,
    answers: Record<string, boolean | string | number | null>,
    scope: 'audit' | 'note' | 'inputs' | 'standing' = 'audit',
  ): Promise<ApiResponse<CorporateFacts>> {
    const r = await put<ApiResponse<CorporateFacts>>(fsaCorporateFactsEndpoints.auditAnswers(engagementId), { answers, scope });
    return r.data;
  }

  /** ADR-0024 R-4b — fill this year's EMPTY record from the previous year's facts (saves). */
  async importFromPrior(engagementId: ID): Promise<ApiResponse<CorporateFactsCarryResult>> {
    const r = await post<ApiResponse<CorporateFactsCarryResult>>(fsaCorporateFactsEndpoints.importFromPrior(engagementId), {});
    return r.data;
  }

  /**
   * ADR-0024 N-5 — store the previous year's opening balances for the analytical
   * ratios from the previous year's saved Balance Sheet (saves; merged server-side).
   */
  async refreshRatioOpenings(engagementId: ID): Promise<ApiResponse<CorporateFactsCarryResult>> {
    const r = await post<ApiResponse<CorporateFactsCarryResult>>(fsaCorporateFactsEndpoints.ratioOpeningsRefresh(engagementId), {});
    return r.data;
  }

  /**
   * ADR-0024 — the master's directors in office during the year (up to the
   * report date), in the default order — to seed or sync the engagement's
   * own directors list. Nothing is saved.
   */
  async getMasterDirectors(engagementId: ID): Promise<MasterDirectorEntry[]> {
    const r = await get<ApiResponse<MasterDirectorEntry[]>>(fsaCorporateFactsEndpoints.masterDirectors(engagementId));
    return r.data?.data ?? [];
  }

  /** Related-party transactions proposed from the Additional Information note — nothing is saved. */
  async proposeRelatedParties(engagementId: ID): Promise<ApiResponse<RelatedPartyImportResult>> {
    const r = await get<ApiResponse<RelatedPartyImportResult>>(
      fsaCorporateFactsEndpoints.relatedPartyImport(engagementId),
    );
    return r.data;
  }

  /** ADR-0024 R-3 — the AGM Notice library (intro, notes, resolution templates). */
  async getAgmLibrary(): Promise<ApiResponse<AgmPackLibrary>> {
    const r = await get<ApiResponse<AgmPackLibrary>>(fsaCorporateFactsEndpoints.agmLibrary());
    return r.data;
  }

  /** ADR-0024 R-3 — AGM pack PDF (notice | proxy | attendance | all), printed from the SAVED facts. */
  async getAgmPackPdf(engagementId: ID, document: 'notice' | 'proxy' | 'attendance' | 'all'): Promise<Blob> {
    const r = await get<Blob>(fsaCorporateFactsEndpoints.agmPackPdf(engagementId, document), { responseType: 'blob' });
    return r.data;
  }

  /** ADR-0024 R-4 — the same document(s) as an editable Word file, downloaded as `fileName`. */
  downloadAgmPackDocx(engagementId: ID, document: 'notice' | 'proxy' | 'attendance' | 'all', fileName: string): Promise<void> {
    return downloadFile(fsaCorporateFactsEndpoints.agmPackDocx(engagementId, document), fileName);
  }

  /**
   * ADR-0024 — save only the sections this screen changed (build the request
   * with factsSections.sectionSaveRequest). 409 names the sections someone
   * else changed since they were read; everything else merges.
   */
  async saveSections(engagementId: ID, request: SaveCorporateFactsSectionsRequest): Promise<ApiResponse<CorporateFacts>> {
    const r = await put<ApiResponse<CorporateFacts>>(fsaCorporateFactsEndpoints.sections(engagementId), request);
    return r.data;
  }

  /** Whole-record save — prefer saveSections. */
  async save(engagementId: ID, request: SaveCorporateFactsRequest): Promise<ApiResponse<CorporateFacts>> {
    const r = await put<ApiResponse<CorporateFacts>>(
      fsaCorporateFactsEndpoints.byEngagement(engagementId), request,
    );
    return r.data;
  }
}

export const fsaCorporateFactsService = new FsaCorporateFactsService();
export default fsaCorporateFactsService;
